<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useSessionStorage } from '@vueuse/core';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert, useTrack } from 'dashboard/composables';
import { ONBOARDING_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import OnboardingShell from 'shared/components/onboarding/OnboardingShell.vue';
import OnboardingInput from 'shared/components/onboarding/OnboardingInput.vue';
import { COMPANY_SIZE_OPTIONS } from './shared/constants';
import {
  captureOnboardingStepAttempted,
  captureOnboardingStepCompleted,
  captureOnboardingStepError,
  captureOnboardingStepViewed,
} from 'dashboard/helpers/onboardingAnalytics';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountId, currentAccount, finishOnboarding, isOnChatwootCloud } =
  useAccount();
const { enabledLanguages } = useConfig();
const globalConfig = useMapGetter('globalConfig/get');
const draft = useSessionStorage(
  'vartana-onboarding-' + accountId.value,
  { name: '', companySize: '' },
  { flush: 'sync' }
);
const signupDraft = useSessionStorage('vartana-signup-draft', {
  email: '',
  fullName: '',
});
const isTeam = computed(() => route.query.step === 'team');
const step = computed(() => (isTeam.value ? 'team' : 'company'));
const isTrial = computed(() => Boolean(currentAccount.value?.trial));
const isSubmitting = ref(false);
const hasMounted = ref(false);
const form = ref(null);
const apiError = ref('');
const v$ = useVuelidate(
  {
    name: { required },
    companySize: {
      required,
      validSize: value =>
        COMPANY_SIZE_OPTIONS.some(option => option.value === value),
    },
  },
  draft
);
const focusFirstInput = () =>
  nextTick(() => form.value?.querySelector('input')?.focus());

onMounted(async () => {
  signupDraft.value = null;
  if (isTeam.value && !draft.value.name.trim()) {
    await router.replace({ query: { ...route.query, step: 'company' } });
  }
  useTrack(ONBOARDING_EVENTS.ACCOUNT_DETAILS_VISITED);
  useTrack(ONBOARDING_EVENTS.STEP_VIEWED, { step: step.value });
  captureOnboardingStepViewed(step.value);
  hasMounted.value = true;
  focusFirstInput();
});
watch(step, value => {
  if (!hasMounted.value) return;

  apiError.value = '';
  useTrack(ONBOARDING_EVENTS.STEP_VIEWED, { step: value });
  captureOnboardingStepViewed(value);
  focusFirstInput();
});

const navigate = value =>
  router.push({ query: { ...route.query, step: value } });

const handleSubmit = async () => {
  if (isSubmitting.value || !currentAccount.value) return;
  isSubmitting.value = true;
  captureOnboardingStepAttempted(step.value);
  apiError.value = '';
  draft.value.name = draft.value.name.trim();
  v$.value.name.$touch();
  if (v$.value.name.$invalid) {
    useTrack(ONBOARDING_EVENTS.STEP_ERROR, {
      step: 'company',
      error_type: 'validation',
    });
    captureOnboardingStepError('company', 'validation');
    if (isTeam.value) await navigate('company');
    isSubmitting.value = false;
    return;
  }
  if (!isTeam.value) {
    try {
      if (isOnChatwootCloud.value && globalConfig.value.isEnterprise) {
        await finishOnboarding({
          name: draft.value.name,
          onboarding_step: 'company_profile',
        });
      }
      useTrack(ONBOARDING_EVENTS.STEP_COMPLETED, { step: 'company' });
      await captureOnboardingStepCompleted('company');
      await navigate('team');
    } catch {
      apiError.value = t('ONBOARDING_NEXT.ERROR');
      useTrack(ONBOARDING_EVENTS.STEP_ERROR, {
        step: 'company',
        error_type: 'api',
      });
      captureOnboardingStepError('company', 'api');
    } finally {
      isSubmitting.value = false;
    }
    return;
  }
  v$.value.companySize.$touch();
  if (v$.value.companySize.$invalid) {
    useTrack(ONBOARDING_EVENTS.STEP_ERROR, {
      step: 'team',
      error_type: 'validation',
    });
    captureOnboardingStepError('team', 'validation');
    isSubmitting.value = false;
    return;
  }

  const browserLocale = navigator.language?.replace('-', '_');
  const localeCodes = enabledLanguages.map(language => language.iso_639_1_code);
  const locale = localeCodes.includes(browserLocale)
    ? browserLocale
    : currentAccount.value.locale;
  const timezone =
    currentAccount.value.custom_attributes?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  try {
    const response = await finishOnboarding({
      name: draft.value.name,
      company_size: draft.value.companySize,
      locale,
      timezone,
      onboarding_step: 'account_details',
    });
    useTrack(ONBOARDING_EVENTS.STEP_COMPLETED, { step: 'team' });
    await captureOnboardingStepCompleted('team');
    useTrack(ONBOARDING_EVENTS.ACCOUNT_DETAILS_COMPLETED, {
      company_size: draft.value.companySize,
    });
    useAlert(
      response?.trial
        ? t('ONBOARDING_NEXT.TRIAL_STARTED', {
            days: response.trial.days_remaining,
          })
        : t('ONBOARDING_NEXT.SUCCESS')
    );
    await store.dispatch('setUser');
    const nextStep = currentAccount.value.custom_attributes?.onboarding_step;
    draft.value = null;
    await router.push({
      name: nextStep === 'inbox_setup' ? 'onboarding_inbox_setup' : 'home',
      params: { accountId: accountId.value },
    });
  } catch {
    apiError.value = t('ONBOARDING_NEXT.ERROR');
    useTrack(ONBOARDING_EVENTS.STEP_ERROR, { step: 'team', error_type: 'api' });
    captureOnboardingStepError('team', 'api');
    isSubmitting.value = false;
  }
};
</script>

<template>
  <OnboardingShell
    :step="isTeam ? 4 : 3"
    :logo="globalConfig.logo"
    :installation-name="globalConfig.installationName"
    authenticated
  >
    <button
      v-if="isTeam"
      type="button"
      class="mb-4 inline-flex min-h-8 items-center gap-2 rounded-md text-sm text-slate-600 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-woot-600 disabled:opacity-50"
      :disabled="isSubmitting"
      @click="navigate('company')"
    >
      <span class="i-lucide-arrow-left size-4" />
      {{ $t('REGISTER.GUIDED.BACK') }}
    </button>
    <h1
      class="mb-3 text-[30px] font-semibold leading-[1.15] tracking-[-0.035em] text-slate-900 sm:text-[34px]"
    >
      {{
        $t(
          isTeam
            ? 'ONBOARDING_NEXT.GUIDED.TEAM_TITLE'
            : 'ONBOARDING_NEXT.GUIDED.COMPANY_TITLE'
        )
      }}
    </h1>
    <p class="mb-6 text-[15px] leading-relaxed text-slate-600">
      {{
        $t(
          isTeam
            ? 'ONBOARDING_NEXT.GUIDED.TEAM_DESCRIPTION'
            : 'ONBOARDING_NEXT.GUIDED.COMPANY_DESCRIPTION'
        )
      }}
    </p>
    <form
      v-if="draft"
      ref="form"
      novalidate
      class="space-y-5"
      @submit.prevent="handleSubmit"
    >
      <OnboardingInput
        v-if="!isTeam"
        v-model="draft.name"
        name="company_name"
        autocomplete="organization"
        :disabled="isSubmitting"
        :label="$t('REGISTER.COMPANY_NAME.LABEL')"
        :placeholder="$t('REGISTER.COMPANY_NAME.PLACEHOLDER')"
        :error="
          v$.name.$error ? $t('ONBOARDING_NEXT.GUIDED.COMPANY_ERROR') : ''
        "
        @blur="v$.name.$touch()"
      />
      <fieldset
        v-else
        :disabled="isSubmitting"
        :aria-describedby="
          v$.companySize.$error ? 'company-size-error' : undefined
        "
      >
        <legend class="mb-3 text-sm font-medium text-slate-900">
          {{ $t('ONBOARDING_NEXT.GUIDED.TEAM_LABEL') }}
        </legend>
        <div class="space-y-2.5">
          <label
            v-for="option in COMPANY_SIZE_OPTIONS"
            :key="option.value"
            class="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm text-slate-900 transition-colors focus-within:ring-4 focus-within:ring-woot-100/60"
            :class="
              draft.companySize === option.value
                ? 'border-woot-600 bg-woot-25 font-medium'
                : 'border-slate-100 bg-slate-25/40 hover:border-woot-600 hover:bg-woot-25/50'
            "
          >
            <input
              v-model="draft.companySize"
              type="radio"
              name="company_size"
              :value="option.value"
              class="!m-0 !size-4 accent-woot-700"
            />
            {{
              option.value === '500+'
                ? $t('ONBOARDING_NEXT.GUIDED.TEAM_LARGE')
                : $t('ONBOARDING_NEXT.GUIDED.TEAM_RANGE', {
                    range: option.label,
                  })
            }}
          </label>
        </div>
        <p
          v-if="v$.companySize.$error"
          id="company-size-error"
          role="alert"
          class="mb-0 mt-2 text-sm text-red-700"
        >
          {{ $t('ONBOARDING_NEXT.GUIDED.TEAM_ERROR') }}
        </p>
      </fieldset>
      <p
        v-if="apiError"
        role="alert"
        class="mb-0 rounded-lg bg-red-50 p-3 text-sm text-red-900"
      >
        {{ apiError }}
      </p>
      <button
        type="submit"
        class="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-woot-500 px-5 py-3 text-[15px] font-semibold text-woot-900 shadow-sm shadow-woot-900/10 transition-colors hover:bg-woot-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-woot-700 disabled:cursor-wait disabled:opacity-60"
        :disabled="isSubmitting || !currentAccount"
        :aria-busy="isSubmitting"
      >
        <span
          v-if="isSubmitting"
          class="i-lucide-loader-circle size-4 animate-spin"
        />
        {{
          $t(
            isSubmitting
              ? 'ONBOARDING_NEXT.SAVING'
              : isTeam && isTrial
                ? 'ONBOARDING_NEXT.GUIDED.START_TRIAL'
                : 'REGISTER.GUIDED.CONTINUE'
          )
        }}
        <span v-if="!isSubmitting" class="i-lucide-arrow-right size-4" />
      </button>
    </form>
    <p
      v-if="isTeam && isTrial"
      class="mb-0 mt-5 text-center text-xs leading-relaxed text-slate-600"
    >
      {{
        $t('ONBOARDING_NEXT.GUIDED.TRIAL_NOTE', {
          days: globalConfig.vartanaTrial.durationDays,
        })
      }}
    </p>
  </OnboardingShell>
</template>
